import type { LiteSecodaEntity, TeamOut } from '@repo/api-codegen';
import { EntityType } from '@repo/common/enums/entityType';
import { pluralize } from '@repo/common/utils';
import { getEntityTypeDisplayInfo } from '@repo/common/utils/entityDisplayUtils';
import type { BreadcrumbCrumb } from '@repo/foundations';
import {
	capitalize,
	find,
	isNil,
	lowerCase,
	map,
	sortBy,
	startCase,
} from 'lodash-es';
import type { IIntegration, INestedBreadcrumbs } from '../api';
import {
	getEntityDisplayTitle,
	isBIDashboard,
	isBIMetric,
	isCatalogEntity,
	isCollection,
	isDatabase,
	isDocument,
	isGlossary,
	isMetric,
	isQuestion,
	isSchema,
} from '../lib/utils/entity';
import { isValidEnumValue } from './enumUtils';
import { buildResourceUrl } from './navigationUtils';
import { getNavigationUrl } from './url';

export interface BreadcrumbEntity {
	id: string;
	entity_type: EntityType;
	title?: string;
	title_cased?: string;
	teams?: Array<string>;
	integration?: string;
}

/**
 * Returns a function that generates a breadcrumb link based on the provided entity, and workspace teams.
 * @param entity The entity object.
 * @param native_type The native type of the entity.
 * @param teamId One of the entity's teams.
 * @returns A function that generates the breadcrumb link.
 */
export const getBreadcrumbLink =
	(
		entity: BreadcrumbEntity | LiteSecodaEntity,
		native_type: string,
		teamId: string | null
	) =>
	(
		data: string | null | undefined,
		entityType?: EntityType | 'integration'
	) => {
		const value = data?.toLowerCase();

		if (isCatalogEntity(entity)) {
			if (entityType === undefined) {
				if (native_type) {
					const filtersArray = [
						{
							value: [native_type],
							operator: 'is',
							filterType: 'native_type',
						},
					];
					const filtersString = JSON.stringify(filtersArray);
					const encodedFilters = encodeURIComponent(filtersString);
					const navigationUrl = `/teams/${teamId}/catalog?filters=${encodedFilters}`;
					return getNavigationUrl(navigationUrl);
				}
				return getNavigationUrl(`/teams/${teamId}/catalog`);
			}

			if (entityType === 'integration') {
				return getNavigationUrl(`/teams/${teamId}/catalog`, {
					filters: {
						integration: [value],
					},
				});
			}

			if (entityType === EntityType.table) {
				return getNavigationUrl(`/table/${value}`);
			}

			if (entityType === EntityType.column) {
				return getNavigationUrl(`/column/${value}`);
			}

			// schema has a specific filter type
			if (entityType === EntityType.schema) {
				return getNavigationUrl(`/schema/${value}`);
			}

			if (entityType === EntityType.database) {
				return getNavigationUrl(`/database/${value}`);
			}

			// other types of filters fallback to parent
			return getNavigationUrl(`/teams/${teamId}/catalog`, {
				filters: {
					parent: [value],
				},
			});
		}

		if (isCollection(entity)) {
			return `/teams/${teamId}/collections`;
		}

		if (isMetric(entity)) {
			return `/teams/${teamId}/metrics`;
		}

		if (isGlossary(entity)) {
			return `/teams/${teamId}/glossary`;
		}

		if (isQuestion(entity)) {
			return `/teams/${teamId}/questions`;
		}

		if (isDocument(entity)) {
			return `/teams/${teamId}/docs`;
		}

		if (isSchema(entity)) {
			return `/teams/${teamId}/catalog?filters=%5B%7B"value"%3A%5B"schema"%5D%2C"operator"%3A"is"%2C"filterType"%3A"native_type"%7D%5D`;
		}

		if (isDatabase(entity)) {
			return `/teams/${teamId}/catalog?filters=%5B%7B"value"%3A%5B"database"%5D%2C"operator"%3A"is"%2C"filterType"%3A"native_type"%7D%5D`;
		}

		if (isBIDashboard(entity)) {
			return `/teams/${teamId}/bi/dashboards`;
		}

		if (isBIMetric(entity)) {
			return `/teams/${teamId}/bi/metrics`;
		}

		// Return an empty string if the entity type is not recognized
		return '';
	};

/**
 * Generates nestedBreadcrumbs for catalog entities.
 * @param type - Entity type.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @param integration - Integration
 * @param nestedBreadcrumbs - Nested breadcrumb objects.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getCatalogEntityBreadcrumbs = (
	native_type: string,
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>,
	entity_type?: EntityType,
	integration?: IIntegration,
	nestedBreadcrumbs: Array<INestedBreadcrumbs> = []
): Array<BreadcrumbCrumb> => {
	let { label } = getEntityTypeDisplayInfo(entity_type);
	if (native_type) {
		label = pluralize(capitalize(lowerCase(startCase(native_type))));
	}

	return [
		{ title: label, href: getBreadcrumbFn(null) },
		{
			title: integration?.name ?? 'Integration',
			href: getBreadcrumbFn(integration?.id, 'integration'),
		},
		...map(nestedBreadcrumbs, (item) => ({
			title: item.title,
			href: getBreadcrumbFn(item.id, item.entity_type),
		})),
	];
};

/**
 * Generates nestedBreadcrumbs for schemas.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getDatabaseBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>
) => [{ title: 'Databases', href: getBreadcrumbFn(null) }];

/**
 * Generates nestedBreadcrumbs for schemas.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getSchemaBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>
) => [{ title: 'Schemas', href: getBreadcrumbFn(null) }];

/**
 * Generates nestedBreadcrumbs for collections.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getCollectionBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>
): Array<BreadcrumbCrumb> => [
	{ title: 'Collections', href: getBreadcrumbFn(null) },
];

/**
 * Generates nestedBreadcrumbs for questions.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getQuestionBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>
): Array<BreadcrumbCrumb> => [
	{ title: 'Questions', href: getBreadcrumbFn(null) },
];

/**
 * Generates nestedBreadcrumbs for metrics.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @param nestedBreadcrumbs - Nested breadcrumb objects. Defaults to empty array.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getMetricBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>,
	nestedBreadcrumbs: Array<INestedBreadcrumbs> = []
): Array<BreadcrumbCrumb> => [
	{
		title: 'Metrics',
		href: getBreadcrumbFn(null),
	},
	...map(nestedBreadcrumbs, (b) => ({
		title: b.title,
		href: `/metrics/${b.id}`,
	})),
];

/**
 * Generates nestedBreadcrumbs for glossary.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @param nestedBreadcrumbs - Nested breadcrumb objects. Defaults to empty array.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getGlossaryBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>,
	nestedBreadcrumbs: Array<INestedBreadcrumbs> = []
): Array<BreadcrumbCrumb> => [
	{
		title: 'Glossary',
		href: getBreadcrumbFn(null),
	},
	...map(nestedBreadcrumbs, (b) => ({
		title: b.title,
		href: `/glossary/${b.id}`,
	})),
];

/**
 * Generates nestedBreadcrumbs for documents.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @param nestedBreadcrumbs - Nested breadcrumb objects. Defaults to empty array.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getDocumentBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>,
	nestedBreadcrumbs: Array<INestedBreadcrumbs> = []
): Array<BreadcrumbCrumb> => [
	{
		title: 'Documents',
		href: getBreadcrumbFn(null),
	},
	...map(nestedBreadcrumbs, (b) => ({
		title: b.title,
		href: `/docs/${b.id}`,
	})),
];

/**
 * Generates nestedBreadcrumbs for BI Dashboards.
 * @param getBreadcrumbFn - Breadcrumb function.
 * @param nestedBreadcrumbs - Nested breadcrumb objects. Defaults to empty array.
 * @returns Array of breadcrumb objects with titles and hrefs.
 */
export const getBIDashboardBreadcrumbs = (
	getBreadcrumbFn: ReturnType<typeof getBreadcrumbLink>,
	nestedBreadcrumbs: Array<INestedBreadcrumbs> = []
): Array<BreadcrumbCrumb> => [
	{
		title: 'Dashboards',
		href: getBreadcrumbFn(null),
	},
	...map(nestedBreadcrumbs, (b) => ({
		title: b.title,
		href: `/bi/dashboards/${b.id}`,
	})),
];

function getTeamBreadcrumbs(team: TeamOut | null): Array<BreadcrumbCrumb> {
	if (team) {
		return [
			{
				icon: team.icon || undefined,
				title: team.name,
				href: `/teams/${team.id}/`,
			},
		];
	}
	return [];
}

interface IgetCurrentPageBreadcrumbs {
	entity: BreadcrumbEntity | LiteSecodaEntity;
}

function getCurrentPageBreadcrumbs({
	entity,
}: IgetCurrentPageBreadcrumbs): Array<BreadcrumbCrumb> {
	if (entity && entity.title && entity.id) {
		const entityUrl = buildResourceUrl(entity);
		return [
			{
				title: getEntityDisplayTitle(entity),
				href: entityUrl,
			},
		];
	}
	return [];
}

interface IgetBreadcrumbForEntityArgs {
	entity: BreadcrumbEntity | LiteSecodaEntity;
	nativeType: string;
	team: TeamOut | null;
	fallbackTeamId?: string | null;
	nestedBreadcrumbs?: Array<INestedBreadcrumbs>;
	integrations?: Array<IIntegration>;
	includeTeam?: boolean;
	includeCurrentPage?: boolean;
}

export function getBreadcrumbForEntity({
	entity,
	nativeType,
	nestedBreadcrumbs,
	team,
	fallbackTeamId = null,
	integrations,
	includeTeam = false,
	includeCurrentPage = false,
}: IgetBreadcrumbForEntityArgs) {
	const teamIdForLink =
		team?.id ??
		fallbackTeamId ??
		(entity as BreadcrumbEntity)?.teams?.[0] ??
		null;
	const getBreadcrumbFn = getBreadcrumbLink(entity, nativeType, teamIdForLink);
	let integration = undefined;

	if (entity.integration && typeof entity.integration === 'string') {
		integration = find(integrations, { id: entity.integration });
	} else if (entity.integration && typeof entity.integration === 'object') {
		integration = find(integrations, { id: entity.integration.id });
	}

	const breadCrumbs = sortBy(nestedBreadcrumbs, ({ level }) => -level);

	const teamBreadcrumbs = includeTeam ? getTeamBreadcrumbs(team) : [];

	const currentPageBreadcrumbs = includeCurrentPage
		? getCurrentPageBreadcrumbs({ entity })
		: [];

	const entityType = isValidEnumValue(EntityType, entity.entity_type)
		? entity.entity_type
		: undefined;

	if (isCatalogEntity(entity)) {
		return teamBreadcrumbs
			.concat(
				getCatalogEntityBreadcrumbs(
					nativeType,
					getBreadcrumbFn,
					entityType,
					integration,
					breadCrumbs
				)
			)
			.concat(currentPageBreadcrumbs);
	}

	if (isCollection(entity)) {
		return teamBreadcrumbs
			.concat(getCollectionBreadcrumbs(getBreadcrumbFn))
			.concat(currentPageBreadcrumbs);
	}

	if (isQuestion(entity)) {
		return teamBreadcrumbs
			.concat(getQuestionBreadcrumbs(getBreadcrumbFn))
			.concat(currentPageBreadcrumbs);
	}

	if (isMetric(entity) && !isNil(breadCrumbs)) {
		return teamBreadcrumbs
			.concat(getMetricBreadcrumbs(getBreadcrumbFn, breadCrumbs))
			.concat(currentPageBreadcrumbs);
	}

	if (isGlossary(entity) && !isNil(breadCrumbs)) {
		return teamBreadcrumbs
			.concat(getGlossaryBreadcrumbs(getBreadcrumbFn, breadCrumbs))
			.concat(currentPageBreadcrumbs);
	}

	if (isDocument(entity) && !isNil(breadCrumbs)) {
		return teamBreadcrumbs
			.concat(getDocumentBreadcrumbs(getBreadcrumbFn, breadCrumbs))
			.concat(currentPageBreadcrumbs);
	}

	if (isBIDashboard(entity) && !isNil(breadCrumbs)) {
		return teamBreadcrumbs
			.concat(getBIDashboardBreadcrumbs(getBreadcrumbFn, breadCrumbs))
			.concat(currentPageBreadcrumbs);
	}

	// eslint-disable-next-line no-console
	console.warn('[breadcrumbs] No breadcrumbs for type');

	return teamBreadcrumbs;
}
